import {
    Box,
    Button,
    Card,
    CardBody,
    Center,
    Heading,
    HStack,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { deletePanel, getPanels, restorePanel } from '../../../api';
import { hasPermission } from '../../../Utils';
import PanelFormModal from './PanelFormModal';
import PanelViewModal from './PanelViewModal';

export default function PanelsPage() {
    const [loading, setLoading] = useState(true);
    const [panels, setPanels] = useState([]);
    const [selected, setSelected] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const toast = useToast();
    const {
        isOpen: isFormOpen,
        onOpen: onFormOpen,
        onClose: onFormClose,
    } = useDisclosure();
    const {
        isOpen: isViewOpen,
        onOpen: onViewOpen,
        onClose: onViewClose,
    } = useDisclosure();
    const {
        isOpen: isDeleteOpen,
        onOpen: onDeleteOpen,
        onClose: onDeleteClose,
    } = useDisclosure();

    const fetchData = async () => {
        setLoading(true);
        const res = await getPanels();
        if (res.success) {
            setPanels(res.data || []);
        } else {
            toast({ status: 'error', title: res.data?.message || 'Failed to fetch panels' });
        }
        setLoading(false);
    };

    useEffect(() => {
        if (hasPermission('viewPanels')) {
            fetchData();
        }
    }, []);

    const confirmDelete = async () => {
        if (!selected) return;
        setDeleting(true);
        const res = await deletePanel(selected._id);
        setDeleting(false);
        onDeleteClose();

        if (res.success) {
            toast({ status: 'success', title: res.data?.message || 'Panel deleted' });
            fetchData();
        } else {
            toast({ status: 'error', title: res.data?.message || 'Delete failed' });
        }
    };

    const handleRestore = async (id) => {
        const res = await restorePanel(id);
        if (res.success) {
            toast({ status: 'success', title: res.data?.message || 'Panel restored' });
            fetchData();
        } else {
            toast({ status: 'error', title: res.data?.message || 'Restore failed' });
        }
    };

    // 🚨 Block page if user lacks viewPanels
    if (!hasPermission('viewPanels')) {
        return (
            <Center>
                <Box p={6}>
                    <Heading textAlign={'center'} size="md" mb={4}>Panels</Heading>
                    <Text textAlign={'center'}>You do not have permission to view panels.</Text>
                </Box>
            </Center>
        );
    }

    return (
        <Box p={6}>
            <HStack justify="space-between" mb={4}>
                <Heading size="md">Panels</Heading>
                {hasPermission('createPanel') && (
                    <Button
                        size="xs"
                        colorScheme="blue"
                        onClick={() => {
                            setSelected(null);
                            onFormOpen();
                        }}
                    >
                        New Panel
                    </Button>
                )}
            </HStack>

            <Card>
                <CardBody>
                    {loading ? (
                        <Spinner />
                    ) : (
                        <Table>
                            <Thead>
                                <Tr>
                                    <Th>Name</Th>
                                    <Th>Description</Th>
                                    <Th>Tests</Th>
                                    <Th>Actions</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {panels.map((panel) => (
                                    <Tr key={panel._id}>
                                        <Td>{panel.name}</Td>
                                        <Td>{panel.description}</Td>
                                        <Td>{panel.tests?.length || 0}</Td>
                                        <Td>
                                            <HStack>
                                                {hasPermission('viewPanels') && (
                                                    <Button
                                                        size="xs"
                                                        colorScheme="green"
                                                        onClick={() => {
                                                            setSelected(panel);
                                                            onViewOpen();
                                                        }}
                                                    >
                                                        View
                                                    </Button>
                                                )}
                                                {hasPermission('updatePanel') && (
                                                    <Button
                                                        size="xs"
                                                        colorScheme="blue"
                                                        onClick={() => {
                                                            setSelected(panel);
                                                            onFormOpen();
                                                        }}
                                                    >
                                                        Edit
                                                    </Button>
                                                )}
                                                {hasPermission('deletePanel') && !panel.deletedAt && (
                                                    <Button
                                                        size="xs"
                                                        colorScheme="red"
                                                        onClick={() => {
                                                            setSelected(panel);
                                                            onDeleteOpen();
                                                        }}
                                                    >
                                                        Delete
                                                    </Button>
                                                )}
                                                {hasPermission('restorePanel') && panel.deletedAt && (
                                                    <Button
                                                        size="xs"
                                                        colorScheme="green"
                                                        onClick={() => handleRestore(panel._id)}
                                                    >
                                                        Restore
                                                    </Button>
                                                )}
                                            </HStack>
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            {/* Create / Edit Modal */}
            {isFormOpen && (
                <PanelFormModal
                    isOpen={isFormOpen}
                    onClose={onFormClose}
                    fetchData={fetchData}
                    panel={selected}
                />
            )}

            {/* View Modal */}
            {isViewOpen && (
                <PanelViewModal
                    isOpen={isViewOpen}
                    onClose={onViewClose}
                    panel={selected}
                />
            )}

            {/* Delete Confirmation Modal */}
            <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Delete Panel</ModalHeader>
                    <ModalBody>
                        Are you sure you want to delete <b>{selected?.name}</b>?
                    </ModalBody>
                    <ModalFooter>
                        <Button mr={3} onClick={onDeleteClose}>Cancel</Button>
                        <Button
                            colorScheme="red"
                            onClick={confirmDelete}
                            isLoading={deleting}
                        >
                            Delete
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}
